import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { showToast } from './Toast';
import { BrandContext, defaultBrand, applyBrandToDocument, KLINIO_DEFAULT_ACCENT } from './BrandContext';
import { hexToRgba, getMonday, isCampaignActive, generateSlots, getDateStr } from './clientUtils';
import { NEUTRAL, STATUS_COLORS, resolveTheme, onAccent } from './designTokens';
import useClientBookingData from './hooks/useClientBookingData';
import { CONSENT_VERSION } from './consent';

import ClientBlockView from './ClientBlockView';
import ClientProgramView from './ClientProgramView';
import ClientHomeTab from './ClientHomeTab';
import ClientProgramsTab from './ClientProgramsTab';
import ClientBookingsTab from './ClientBookingsTab';
import ClientMessagesTab from './ClientMessagesTab';
import ClientHero, { CLIENT_HERO_OVERLAP } from './ClientHero';

function ClientView({ session }) {
  const [client, setClient] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedCp, setSelectedCp] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [blockExercises, setBlockExercises] = useState([]);
  const [exerciseLogs, setExerciseLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [notifCounts, setNotifCounts] = useState({ messages: 0, bookings: 0, programs: 0 });

  // Bokning
  const { schedules, bookings, cancelledBookings, blockedTimes, services, bookedSlots, loading: bookingsLoading, error: bookingError, refetch: refetchBookingData } = useClientBookingData(client?.therapist_id);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBookingDate, setSelectedBookingDate] = useState(null);
  const [selectedWeekStart, setSelectedWeekStart] = useState(getMonday(new Date()));
  const [brand, setBrand] = useState(defaultBrand);
  const [weekSessionCounts, setWeekSessionCounts] = useState({});
  const [todayDonePrograms, setTodayDonePrograms] = useState({});
  const [weekSessionDays, setWeekSessionDays] = useState({}); // program_id -> [dateStr]
  const [confirmingSlot, setConfirmingSlot] = useState(null);
  const [cancellingBookingId, setCancellingBookingId] = useState(null);
  const [cancellationFeedback, setCancellationFeedback] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [bookingNotice, setBookingNotice] = useState(null);
  const deepLinkHandled = useRef(false);

  // Anamnes
  const [anamnesisTemplate, setAnamnesisTemplate] = useState(null);
  const [anamnesisQuestions, setAnamnesisQuestions] = useState([]);
  const [anamnesisResponse, setAnamnesisResponse] = useState(null);
  const [anamnesisAnswers, setAnamnesisAnswers] = useState({});
  const [anamnesisLoading, setAnamnesisLoading] = useState(false);
  const [anamnesisSubmitting, setAnamnesisSubmitting] = useState(false);
  const [showAnamnesisForm, setShowAnamnesisForm] = useState(false);
  const [anamnesisJustSubmitted, setAnamnesisJustSubmitted] = useState(false);
  const [anamnesisConsent, setAnamnesisConsent] = useState(false);

  useEffect(() => {
    fetchClientData();
  }, []);

  const fetchClientData = async () => {
    setLoading(true);
    setFetchError(null);

    try {
      const { data: clientData } = await supabase
        .from('clients')
        .select('*')
        .ilike('email', session.user.email)
        .single();

      if (!clientData) {
        setNotFound(true);
        return;
      }

      setClient(clientData);

      const { data: brandData } = await supabase
        .from('brand_settings')
        .select('*')
        .eq('therapist_id', clientData.therapist_id)
        .maybeSingle();
      if (brandData) {
        const b = {
          displayName: brandData.display_name || null,
          logoUrl: brandData.logo_url || null,
          logoDarkUrl: brandData.logo_dark_url || null,
          iconUrl: brandData.icon_url || null,
          tagline: brandData.tagline || null,
          accentColor: brandData.accent_color || null,
          cancellationInfo: brandData.cancellation_info || null,
        };
        setBrand(b);
        applyBrandToDocument(b);
      }

      const { data: cpData } = await supabase
        .from('client_programs')
        .select('*, program:programs(*)')
        .eq('client_id', clientData.id);

      setPrograms(cpData || []);

      // Hämta all bokningsdata direkt vid inloggning så startsidan kan visa nästa tid
      await refetchBookingData(clientData.therapist_id);

      fetchNotifCounts(clientData);
      fetchAnamnesisData(clientData);
      fetchWeekSessionCounts(clientData);
    } catch (err) {
      console.error('[fetchClientData]', err);
      setFetchError(err.message || 'Kunde inte ladda din profil.');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifCounts = async (clientData) => {
    if (!clientData) return;
    const [{ count: msgCount }, { count: bookCount }, { count: progCount }] = await Promise.all([
      supabase.from('messages').select('*', { count: 'exact', head: true })
        .eq('client_id', clientData.id).eq('sender_role', 'therapist').eq('read', false),
      supabase.from('bookings').select('*', { count: 'exact', head: true })
        .eq('client_id', clientData.id).eq('client_seen', false).neq('status', 'cancelled'),
      supabase.from('client_programs').select('*', { count: 'exact', head: true })
        .eq('client_id', clientData.id).eq('client_seen', false),
    ]);
    setNotifCounts({ messages: msgCount || 0, bookings: bookCount || 0, programs: progCount || 0 });
  };

  const fetchAnamnesisData = async (clientData) => {
    const c = clientData || client;
    if (!c) return;
    setAnamnesisLoading(true);

    const { data: tmpl } = await supabase
      .from('anamnesis_templates')
      .select('*')
      .eq('therapist_id', c.therapist_id)
      .maybeSingle();
    setAnamnesisTemplate(tmpl || null);

    if (tmpl) {
      const { data: questions } = await supabase
        .from('anamnesis_questions')
        .select('*')
        .eq('template_id', tmpl.id)
        .order('sort_order');
      setAnamnesisQuestions(questions || []);

      const { data: resp } = await supabase
        .from('anamnesis_responses')
        .select('*, answers:anamnesis_answers(*)')
        .eq('therapist_id', c.therapist_id)
        .eq('client_id', c.id)
        .maybeSingle();
      setAnamnesisResponse(resp || null);

      if (resp) {
        const existing = {};
        (resp.answers || []).forEach(a => { existing[a.question_id] = a.answer_text || ''; });
        setAnamnesisAnswers(existing);
      }
    }
    setAnamnesisLoading(false);
  };

  const fetchWeekSessionCounts = async (clientData) => {
    const c = clientData || client;
    if (!c) return;
    const monday = getMonday(new Date());
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    const { data: weekLogs } = await supabase
      .from('exercise_logs')
      .select('program_id, completed_at')
      .eq('client_id', c.id)
      .gte('completed_at', fmt(monday) + 'T00:00:00')
      .lte('completed_at', fmt(sunday) + 'T23:59:59');
    if (!weekLogs) return;
    const dateSets = {};
    weekLogs.forEach(log => {
      const dateStr = log.completed_at.split('T')[0];
      if (!dateSets[log.program_id]) dateSets[log.program_id] = new Set();
      dateSets[log.program_id].add(dateStr);
    });
    const counts = {};
    const todayDone = {};
    const todayStr = fmt(new Date());
    Object.keys(dateSets).forEach(pid => {
      counts[pid] = dateSets[pid].size;
      todayDone[pid] = dateSets[pid].has(todayStr);
    });
    setWeekSessionCounts(counts);
    setTodayDonePrograms(todayDone);
    const days = {};
    Object.keys(dateSets).forEach(pid => { days[pid] = [...dateSets[pid]]; });
    setWeekSessionDays(days);
  };

  const fetchMessages = async (silent = false) => {
    if (!silent) setMessagesLoading(true);
    try {
      const { data } = await supabase
        .from('messages')
        .select('*')
        .eq('client_id', client.id)
        .eq('therapist_id', client.therapist_id)
        .order('created_at', { ascending: true });
      setMessages(data || []);
    } finally {
      if (!silent) setMessagesLoading(false);
    }
    const { error: readError } = await supabase
      .from('messages')
      .update({ read: true })
      .eq('client_id', client.id)
      .eq('therapist_id', client.therapist_id)
      .eq('sender_role', 'therapist')
      .eq('read', false);
    if (readError) console.error('[fetchMessages] read update failed:', readError);
  };

  // T-3: djuplänk från hemsidan (?service=&date=&time=) – läses från localStorage (satt i App.js)
  // och valideras mot terapeutens riktiga data innan något förväljs. Körs en gång per session.
  useEffect(() => {
    if (deepLinkHandled.current || !client || bookingsLoading || services.length === 0) return;
    const raw = localStorage.getItem('fr_pending_booking');
    if (!raw) return;
    deepLinkHandled.current = true;
    localStorage.removeItem('fr_pending_booking');

    let pending;
    try { pending = JSON.parse(raw); } catch (_) { return; }
    const service = services.find(s => s.id === pending?.service);
    if (!service) return;

    setSelectedService(service);
    setActiveTab('bookings');

    const todayStr = getDateStr(new Date());
    if (!pending.date || pending.date < todayStr) return;
    const [y, m, d] = pending.date.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (Number.isNaN(date.getTime()) || getDateStr(date) !== pending.date) return;

    setSelectedBookingDate(date);
    setSelectedWeekStart(getMonday(date));
    if (!pending.time) return;

    const schedule = schedules.find(s => s.day_of_week === date.getDay());
    const breakDuration = schedules.length > 0 ? schedules[0].break_duration : 15;
    const slot = generateSlots(schedule, service.duration, breakDuration).find(s => s.start === pending.time);
    const overlaps = (list) => list.some(b => {
      if (b.date !== pending.date) return false;
      if (b.all_day) return true;
      const bStart = b.start_time?.slice(0, 5), bEnd = b.end_time?.slice(0, 5);
      return slot && slot.start < bEnd && slot.end > bStart;
    });
    const nowStr = String(new Date().getHours()).padStart(2, '0') + ':' + String(new Date().getMinutes()).padStart(2, '0');
    const isPast = pending.date === todayStr && pending.time < nowStr;

    if (slot && !isPast && !overlaps(bookedSlots || []) && !overlaps(blockedTimes || [])) {
      setConfirmingSlot({ start: slot.start, end: slot.end });
    } else {
      setBookingNotice('Tiden du valde är inte längre ledig – välj en annan tid.');
    }
  }, [client, bookingsLoading, services, schedules, bookedSlots, blockedTimes]);

  useEffect(() => {
    if (activeTab !== 'messages' || !client) return;
    const interval = setInterval(() => fetchMessages(true), 10000);
    return () => clearInterval(interval);
  }, [activeTab, client]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    const { data: inserted, error } = await supabase.from('messages').insert({
      therapist_id: client.therapist_id,
      client_id: client.id,
      sender_role: 'client',
      content: newMessage.trim(),
    }).select('id').single();
    if (error) showToast('Fel: ' + error.message, 'error');
    else {
      setNewMessage('');
      fetchMessages();
      if (inserted?.id) {
        supabase.functions.invoke('send-message-notification', {
          body: { message_id: inserted.id, app_url: window.location.origin },
        }).then(({ error: notifErr }) => {
          if (notifErr) console.error('[msg-notify] Fel från Edge Function:', notifErr.message, notifErr);
        }).catch(err => console.error('[msg-notify] Nätverksfel:', err));
      } else {
        console.warn('[msg-notify] inserted?.id saknas, invoke hoppas över. inserted:', inserted);
      }
    }
  };

  const openProgram = async (cp) => {
    setSelectedCp(cp);
    setSelectedProgram(cp.program);
    setSelectedBlock(null);

    // Markera som sett när kunden faktiskt öppnar programmet
    if (!cp.client_seen) {
      await supabase.rpc('mark_client_program_seen', { cp_id: cp.id });
      setPrograms(prev => prev.map(p => p.id === cp.id ? { ...p, client_seen: true } : p));
      fetchNotifCounts(client);
    }

    const { data: blocksData } = await supabase
      .from('program_blocks')
      .select('*')
      .eq('program_id', cp.program.id)
      .order('sort_order');
    setBlocks(blocksData || []);
  };

  const openBlock = async (block) => {
    setSelectedBlock(block);

    const { data: peData } = await supabase
      .from('program_exercises')
      .select('*, exercise:exercises(*)')
      .eq('block_id', block.id)
      .order('sort_order');
    setBlockExercises(peData || []);

    const today = new Date().toISOString().split('T')[0];
    const { data: logsData } = await supabase
      .from('exercise_logs')
      .select('*')
      .eq('client_id', client.id)
      .eq('program_id', selectedProgram.id)
      .eq('block_id', block.id)
      .gte('completed_at', today + 'T00:00:00')
      .lte('completed_at', today + 'T23:59:59');
    setExerciseLogs(logsData || []);
  };

  const toggleExercise = async (exerciseId) => {
    const existing = exerciseLogs.find(l => l.exercise_id === exerciseId);
    if (existing) {
      await supabase.from('exercise_logs').delete().eq('id', existing.id);
    } else {
      await supabase.from('exercise_logs').insert({
        client_id: client.id,
        program_id: selectedProgram.id,
        block_id: selectedBlock.id,
        exercise_id: exerciseId,
      });
    }
    openBlock(selectedBlock);
    fetchWeekSessionCounts();
  };

  const markAllDone = async () => {
    const unchecked = blockExercises.filter(pe => !exerciseLogs.find(l => l.exercise_id === pe.exercise_id));
    if (unchecked.length === 0) return;
    const inserts = unchecked.map(pe => ({
      client_id: client.id,
      program_id: selectedProgram.id,
      block_id: selectedBlock.id,
      exercise_id: pe.exercise_id,
    }));
    await supabase.from('exercise_logs').insert(inserts);
    openBlock(selectedBlock);
    fetchWeekSessionCounts();
  };

  const bookSlot = async (dateStr, slotStart, slotEnd) => {
    setConfirmingSlot(null);
    const campaignActive = isCampaignActive(selectedService);
    const { data: newBooking, error } = await supabase.from('bookings').insert({
      therapist_id: client.therapist_id,
      client_id: client.id,
      date: dateStr,
      start_time: slotStart,
      end_time: slotEnd,
      service_id: selectedService?.id || null,
      booked_by: 'client',
      client_seen: true,
      therapist_seen: false,
      service_title_snapshot: selectedService?.title ?? null,
      price_snapshot: selectedService?.price ?? null,
      currency_snapshot: selectedService?.currency ?? null,
      campaign_active_snapshot: selectedService ? campaignActive : null,
      campaign_price_snapshot: campaignActive ? selectedService.campaign_price : null,
    }).select('id').single();

    if (error) {
      const msg = (error.code === '23505' || error.code === '23P01')
        ? 'Tyvärr, den tiden togs precis. Välj en annan tid.'
        : 'Fel: ' + error.message;
      showToast(msg, 'error');
      refetchBookingData();
    } else {
      setBookingConfirmed({
        id: newBooking?.id || null,
        date: dateStr,
        startTime: slotStart,
        endTime: slotEnd,
        service: selectedService || null,
        price_snapshot: selectedService?.price ?? null,
        currency_snapshot: selectedService?.currency ?? null,
        campaign_active_snapshot: selectedService ? campaignActive : null,
        campaign_price_snapshot: campaignActive ? selectedService.campaign_price : null,
      });
      refetchBookingData();
      setSelectedBookingDate(null);
      setBookingNotice(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (newBooking?.id) {
        supabase.functions.invoke('send-booking-confirmation', {
          body: { booking_id: newBooking.id, therapist_id: client.therapist_id },
        }).catch(err => console.error('[booking-confirm-client] Fel vid skickande av bokningsbekräftelse:', err));
      }
    }
  };

  const cancelMyBooking = async (bookingId) => {
    // therapist_seen=false så att avbokningen dyker upp under "Att hantera" på terapeutens Hem (T-12)
    const { error } = await supabase.from('bookings').update({ status: 'cancelled', therapist_seen: false }).eq('id', bookingId);
    if (error) { showToast('Fel vid avbokning: ' + error.message, 'error'); return; }
    setCancellingBookingId(null);
    setCancellationFeedback(true);
    setTimeout(() => setCancellationFeedback(false), 3500);
    refetchBookingData();
    supabase.functions.invoke('send-cancellation-notification', {
      body: { booking_id: bookingId },
    }).catch(err => console.error('[cancellation-notify] Fel vid skickande av avbokningsnotis:', err));
  };

  const submitAnamnesis = async () => {
    if (!client || !anamnesisTemplate) return;
    const required = anamnesisQuestions.filter(q => q.is_required);
    const missing = required.some(q => !anamnesisAnswers[q.id]?.trim());
    if (missing) { showToast('Fyll i alla obligatoriska fält.', 'error'); return; }
    if (!anamnesisConsent) { showToast('Du behöver samtycka till att dina hälsouppgifter behandlas.', 'error'); return; }

    setAnamnesisSubmitting(true);
    const now = new Date().toISOString();
    const { data: resp, error: respError } = await supabase
      .from('anamnesis_responses')
      .insert({ therapist_id: client.therapist_id, client_id: client.id, template_id: anamnesisTemplate.id, filled_by: 'client', submitted_at: now, consent_given_at: now, consent_version: CONSENT_VERSION })
      .select().single();

    if (respError) { showToast('Fel: ' + respError.message, 'error'); setAnamnesisSubmitting(false); return; }

    const answers = anamnesisQuestions.map(q => ({
      response_id: resp.id,
      question_id: q.id,
      answer_text: anamnesisAnswers[q.id] || '',
    }));
    await supabase.from('anamnesis_answers').insert(answers);
    await fetchAnamnesisData();
    setAnamnesisJustSubmitted(true);
    setTimeout(() => setAnamnesisJustSubmitted(false), 4000);
    setAnamnesisSubmitting(false);
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setSelectedProgram(null);
    setSelectedCp(null);
    setSelectedBlock(null);
    setSelectedBookingDate(null);
    setSelectedService(null);
    setConfirmingSlot(null);
    setBookingConfirmed(null);
    setBookingNotice(null);

    if (tab === 'messages') {
      setNotifCounts(prev => ({ ...prev, messages: 0 }));
      if (client) {
        await fetchMessages(); // hämtar för display + markerar som lästa i DB
        fetchNotifCounts(client); // verifiera mot DB efter uppdatering
      }
    }

    if (tab === 'bookings') {
      setNotifCounts(prev => ({ ...prev, bookings: 0 }));
      if (client) {
        refetchBookingData();
        const { error: seenError } = await supabase.from('bookings')
          .update({ client_seen: true })
          .eq('client_id', client.id)
          .eq('client_seen', false);
        if (seenError) console.error('[handleTabChange] bookings client_seen update failed:', seenError);
        fetchNotifCounts(client); // verifiera mot DB efter uppdatering
      }
    }

    // fetchNotifCounts anropas efter awaitad DB-uppdatering (ingen race condition).
    // Badges: optimistisk nollning ger omedelbar UX, fetchNotifCounts verifierar
    // att ändringen faktiskt persisterades. Vid mount och openProgram sker samma.
  };

  const activeDuration = selectedService ? selectedService.duration : (services.length > 0 ? Math.min(...services.map(s => s.duration)) : 60);
  const activeBreak = schedules.length > 0 ? schedules[0].break_duration : 15;

  const brandAccent = brand.accentColor || KLINIO_DEFAULT_ACCENT;
  const brandTint = hexToRgba(brandAccent, 0.08);
  const brandTintBorder = hexToRgba(brandAccent, 0.28);
  const theme = resolveTheme(brandAccent);

  // ─── Laddning ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: NEUTRAL.page }}>
      <p style={{ color: NEUTRAL.muted, fontSize: '0.95em' }}>Laddar...</p>
    </div>
  );

  // ─── Laddningsfel ─────────────────────────────────────────────────────────────
  if (fetchError) return (
    <div style={{ maxWidth: '480px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '40px 32px', border: `1px solid ${NEUTRAL.line}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <p style={{ margin: '0 0 8px', fontWeight: '600', color: NEUTRAL.ink, fontSize: '1em' }}>Något gick fel</p>
        <p style={{ margin: '0 0 24px', color: NEUTRAL.muted, fontSize: '0.9em', lineHeight: '1.6' }}>
          Din profil kunde inte laddas. Kontrollera din internetanslutning och försök igen.
        </p>
        <button
          onClick={fetchClientData}
          style={{ background: STATUS_COLORS.info, color: 'white', border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9em', fontWeight: '500' }}
        >
          Försök igen
        </button>
      </div>
    </div>
  );

  // ─── Ej kopplat konto ─────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '40px 32px', border: `1px solid ${NEUTRAL.line}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{
            width: '48px', height: '48px',
            background: NEUTRAL.page,
            borderRadius: '14px',
            margin: '0 auto 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={NEUTRAL.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <h2 style={{ margin: '0 0 10px', color: NEUTRAL.ink, fontSize: '1.15em', fontWeight: '700' }}>Kontot är verifierat</h2>
          <p style={{ color: NEUTRAL.muted, margin: '0 0 8px', lineHeight: '1.6', fontSize: '0.92em' }}>
            Din e-post är bekräftad, men kontot är ännu inte kopplat till en terapeut.
          </p>
          <p style={{ color: NEUTRAL.muted, margin: '0 0 28px', lineHeight: '1.6', fontSize: '0.87em' }}>
            Öppna inbjudningslänken från din terapeut igen för att slutföra kopplingen, eller kontakta din terapeut direkt.
          </p>
          <button
            onClick={() => supabase.auth.signOut()}
            style={{ background: 'white', color: STATUS_COLORS.info, border: `1px solid ${STATUS_COLORS.info}40`, padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9em', fontWeight: '500' }}
          >
            Logga ut
          </button>
        </div>
      </div>
    );
  }

  // ─── Sub-vyer (tar över hela skärmen) ────────────────────────────────────────
  if (selectedBlock) {
    const reviewMode = (selectedCp?.frequency_per_week > 0) &&
      (weekSessionCounts[selectedProgram?.id] || 0) >= selectedCp.frequency_per_week;
    return (
      <ClientBlockView
        selectedBlock={selectedBlock}
        selectedCp={selectedCp}
        selectedProgram={selectedProgram}
        blockExercises={blockExercises}
        exerciseLogs={exerciseLogs}
        weekSessionCounts={weekSessionCounts}
        toggleExercise={toggleExercise}
        markAllDone={markAllDone}
        onBack={() => setSelectedBlock(null)}
        brandAccent={brandAccent}
        theme={theme}
        reviewMode={reviewMode}
      />
    );
  }

  if (selectedProgram && selectedCp) {
    return (
      <ClientProgramView
        selectedProgram={selectedProgram}
        selectedCp={selectedCp}
        blocks={blocks}
        weekSessionCounts={weekSessionCounts}
        openBlock={openBlock}
        onBack={() => { setSelectedProgram(null); setSelectedCp(null); }}
        brandAccent={brandAccent}
        brand={brand}
        theme={theme}
      />
    );
  }

  // ─── Huvudvy ─────────────────────────────────────────────────────────────────
  return (
    <BrandContext.Provider value={brand}>
      {/* T-16: hela sidan i samma grå ton på dator (tidigare vit kant runt en grå mittkolumn) */}
      <div style={{ background: NEUTRAL.page, minHeight: '100vh' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '24px 16px' }}>

        {/* ── Mörkt sidhuvud per flik (T-13) ── */}
        {(() => {
          const todayStr = getDateStr(new Date());
          const now = new Date();
          const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
          const DAYS = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];
          const firstName = (client.name || '').split(' ')[0];
          const myNext = bookings
            .filter(b => b.client_id === client.id && b.date >= todayStr)
            .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time))[0] || null;
          const activeCp = programs.find(cp => !cp.end_date || cp.end_date >= todayStr) || null;
          const sessions = activeCp ? (weekSessionCounts[activeCp.program?.id] || 0) : 0;
          const tomorrowStr = getDateStr(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
          const nextLabel = !myNext ? null
            : myNext.date === todayStr ? `idag ${myNext.start_time.slice(0, 5)}`
            : myNext.date === tomorrowStr ? `imorgon ${myNext.start_time.slice(0, 5)}`
            : `${DAYS[new Date(myNext.date + 'T12:00:00').getDay()].toLowerCase().slice(0, 3)} ${new Date(myNext.date + 'T12:00:00').getDate()} ${MONTHS[new Date(myNext.date + 'T12:00:00').getMonth()].slice(0, 3)} ${myNext.start_time.slice(0, 5)}`;
          const homeSub = [
            nextLabel ? `Nästa besök ${nextLabel}` : 'Ingen bokad tid',
            activeCp?.frequency_per_week > 0 ? `${sessions} av ${activeCp.frequency_per_week} pass klara denna vecka` : null,
          ].filter(Boolean).join(' · ');
          const activeCount = programs.filter(cp => !cp.end_date || cp.end_date >= todayStr).length;
          const byTab = {
            home:     { label: `${DAYS[now.getDay()]} ${now.getDate()} ${MONTHS[now.getMonth()]}`, title: `Hej ${firstName}`, sub: homeSub },
            bookings: { label: brand.displayName || 'Bokning', title: 'Boka tid', sub: 'Välj behandling och en tid som passar dig' },
            programs: { label: brand.displayName || 'Träning', title: 'Mina program', sub: activeCount > 0 ? `${activeCount} ${activeCount === 1 ? 'aktivt program' : 'aktiva program'}` : 'Inga program tilldelade ännu' },
            messages: { label: brand.displayName || 'Chatt', title: 'Meddelanden', sub: brand.displayName ? `Skriv till ${brand.displayName}` : 'Skriv till din terapeut' },
          };
          const h = byTab[activeTab] || byTab.home;
          return <ClientHero brand={brand} accent={brandAccent} onLogout={() => supabase.auth.signOut()} label={h.label} title={h.title} sub={h.sub} />;
        })()}

        {/* Fliknavigation – överst på dator, bottenmeny på mobil (se App.css .fr-client-*) */}
        <div className="fr-client-tabs" style={{ display: 'inline-flex', gap: '4px', background: NEUTRAL.card, border: `1px solid ${NEUTRAL.line}`, padding: '4px', borderRadius: '10px', margin: `${-CLIENT_HERO_OVERLAP}px 12px 16px`, position: 'relative', flexWrap: 'wrap', maxWidth: 'calc(100% - 24px)', boxSizing: 'border-box' }}>
          {(() => {
            const badge = (count) => count > 0 ? (
              <span style={{ background: STATUS_COLORS.error, color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '0.7em', marginLeft: '5px', fontWeight: '700', verticalAlign: 'middle' }}>
                {count > 9 ? '9+' : count}
              </span>
            ) : null;

            const tabs = [
              { id: 'home', label: 'Hem', count: 0 },
              { id: 'programs', label: 'Program', count: notifCounts.programs },
              { id: 'bookings', label: 'Boka tid', count: notifCounts.bookings },
              { id: 'messages', label: 'Meddelanden', count: notifCounts.messages },
            ];

            return tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                style={{
                  padding: '7px 14px',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  background: activeTab === tab.id ? theme.accent_soft_bg : 'transparent',
                  color: activeTab === tab.id ? theme.accent_soft_text : NEUTRAL.muted,
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}{badge(tab.count)}
              </button>
            ));
          })()}
        </div>

        <nav className="fr-client-bottomnav" aria-label="Huvudmeny">
          {[
            { id: 'home', label: 'Hem', count: 0, icon: <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" /> },
            { id: 'bookings', label: 'Boka', count: notifCounts.bookings, icon: <><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></> },
            { id: 'programs', label: 'Program', count: notifCounts.programs, icon: <path d="M4 6h16M4 12h10M4 18h7" /> },
            { id: 'messages', label: 'Meddelanden', count: notifCounts.messages, icon: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.2A8 8 0 1 1 21 12z" /> },
          ].map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                aria-current={active ? 'page' : undefined}
                style={{ color: active ? brandAccent : NEUTRAL.muted, fontWeight: active ? 700 : 500 }}
              >
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{tab.icon}</svg>
                  {tab.count > 0 && (
                    <span style={{ position: 'absolute', top: '-5px', right: '-9px', minWidth: '16px', height: '16px', borderRadius: '999px', background: brandAccent, color: onAccent(brandAccent), fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', boxSizing: 'border-box' }}>
                      {tab.count > 9 ? '9+' : tab.count}
                    </span>
                  )}
                </span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Flikinnehåll */}
        <div className="fr-client-content">
        {activeTab === 'home' && (
          <ClientHomeTab
            client={client}
            bookings={bookings}
            programs={programs}
            notifCounts={notifCounts}
            cancellingBookingId={cancellingBookingId}
            setCancellingBookingId={setCancellingBookingId}
            cancellationFeedback={cancellationFeedback}
            cancelMyBooking={cancelMyBooking}
            handleTabChange={handleTabChange}
            openProgram={openProgram}
            setActiveTab={setActiveTab}
            weekSessionCounts={weekSessionCounts}
            weekSessionDays={weekSessionDays}
            brandAccent={brandAccent}
            brandTintBorder={brandTintBorder}
            theme={theme}
          />
        )}

        {activeTab === 'programs' && (
          <ClientProgramsTab
            programs={programs}
            openProgram={openProgram}
            weekSessionCounts={weekSessionCounts}
            brandAccent={brandAccent}
            brandTint={brandTint}
            brandTintBorder={brandTintBorder}
            theme={theme}
          />
        )}

        {activeTab === 'bookings' && bookingError && (
          <p style={{ color: STATUS_COLORS.error, padding: '16px 0', fontSize: '0.9em' }}>
            Kunde inte ladda bokningsdata: {bookingError}
          </p>
        )}
        {activeTab === 'bookings' && !bookingError && (
          <ClientBookingsTab
            client={client}
            bookings={bookings}
            bookedSlots={bookedSlots}
            cancelledBookings={cancelledBookings}
            schedules={schedules}
            blockedTimes={blockedTimes}
            services={services}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            selectedWeekStart={selectedWeekStart}
            setSelectedWeekStart={setSelectedWeekStart}
            selectedBookingDate={selectedBookingDate}
            setSelectedBookingDate={setSelectedBookingDate}
            confirmingSlot={confirmingSlot}
            setConfirmingSlot={setConfirmingSlot}
            bookSlot={bookSlot}
            bookingConfirmed={bookingConfirmed}
            setBookingConfirmed={setBookingConfirmed}
            bookingNotice={bookingNotice}
            setBookingNotice={setBookingNotice}
            brand={brand}
            bookingsLoading={bookingsLoading}
            anamnesisTemplate={anamnesisTemplate}
            anamnesisResponse={anamnesisResponse}
            anamnesisJustSubmitted={anamnesisJustSubmitted}
            anamnesisLoading={anamnesisLoading}
            anamnesisQuestions={anamnesisQuestions}
            anamnesisAnswers={anamnesisAnswers}
            setAnamnesisAnswers={setAnamnesisAnswers}
            showAnamnesisForm={showAnamnesisForm}
            anamnesisConsent={anamnesisConsent}
            setAnamnesisConsent={setAnamnesisConsent}
            setShowAnamnesisForm={setShowAnamnesisForm}
            anamnesisSubmitting={anamnesisSubmitting}
            submitAnamnesis={submitAnamnesis}
            cancellingBookingId={cancellingBookingId}
            setCancellingBookingId={setCancellingBookingId}
            cancellationFeedback={cancellationFeedback}
            cancelMyBooking={cancelMyBooking}
            activeDuration={activeDuration}
            activeBreak={activeBreak}
            brandAccent={brandAccent}
            brandTint={brandTint}
            brandTintBorder={brandTintBorder}
            theme={theme}
          />
        )}

        {activeTab === 'messages' && (
          <ClientMessagesTab
            messages={messages}
            messagesLoading={messagesLoading}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            sendMessage={sendMessage}
            brand={brand}
            brandAccent={brandAccent}
            brandTint={brandTint}
          />
        )}
        </div>
      </div>
      </div>
    </BrandContext.Provider>
  );
}

export default ClientView;
